import { Inject } from '@odinjs/odin';
import { LocalizedLabelProps, ModalService, PanelAPI, TextAPI, ThemeColor, ThemeService, ThemeTonalRange } from '@philips-internal/oswald-ui-components';
import { SupportedLocale, ToastLevel } from '@philips-internal/oswald-ui-constants';
import Controller, { OnCatch, OnMount, OnSuccess, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent, RequestEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AuthenticationService, HttpService, JWTToken } from '@philips-internal/oswald-ui-core/lib/services';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/message-service';
import { LoginAPI } from '@philips-internal/oswald-ui-foundation';

import { BRAZIL_LABEL_DATA, brazilLabelEnum, MANIFEST, ManifestJson, UNSPECIFIED } from './login-types';
import BaseController from '../../BaseController'
import { LoginFoundationAliases as Alias, FragmentNames } from '../../../codegen';
import { Establishment } from '../../../interfaces/establishment';

import './login-location.css';
import { Endpoint } from '../../../constants/endpoints';
import { Token } from '../../../constants/token';
import { YesNoType } from '../../../utils/enum-utils';
import { Value } from '@philips-internal/oswald-ui-core';

@Controller({ component: Alias.PANEL_LOGIN, fragment: FragmentNames.LOGIN_FOUNDATION })
class LoginController extends BaseController {

  @Inject(Alias.ESTABLISHMENT_PANEL)
  private establishmentPanelAPI!: PanelAPI;

  @OnMount({ target: Alias.PANEL_LOGIN })
  handleMountLogin(): void {
    ThemeService.setColors(ThemeColor.GROUP_BLUE, ThemeColor.GREEN);
    ThemeService.setContentTonalRange(ThemeTonalRange.BRIGHT);
    this.getEstablishmentDeault();
  }

  @OnMount({ locales: [SupportedLocale.PT_BR], target: Alias.LOGIN_FOUNDATION})
  handleMountLoginFoundationPtBr(event: MountEvent): void {
    this.handleBrazilLabelData();
    const loginAPI = event.target as LoginAPI;
    loginAPI.setAboutBoxProps({
      companyAddressOutlineIcon: '/static/assets/manufacturer-brazil-symbol-transparent.svg',
      customIcons: [
        '/static/assets/warning_iso.svg',
        '/static/assets/DLS_Brand_Warning.svg',
        '/static/assets/manufacturer-brazil-symbol-transparent.svg'
      ],
      localizedLabel: BRAZIL_LABEL_DATA,
      showQuantity: true,
      versionExpression: `${MessageService.translate(brazilLabelEnum.productVersion)}`,
      companyAddress: MessageService.translate(BRAZIL_LABEL_DATA.data[3].value),
      logoIcon: '/static/assets/branding-logo.svg',
      productName: '',
    });
  }

  @OnMount(Alias.LOGIN_FOUNDATION)
  handleMountLoginFoundation(event: MountEvent): void {
    const login = event.target as LoginAPI;
    login.setAboutBoxProps({
      companyAddressOutlineIcon: '/static/assets/manufacturer-brazil-symbol-transparent.svg',
      customIcons: [
        '/static/assets/warning_iso.svg',
        '/static/assets/DLS_Brand_Warning.svg',
        '/static/assets/manufacturer-brazil-symbol-transparent.svg'
      ],
      productName: ' ',
      companyAddress: MessageService.translate('tws.about-box.company-address'),
      logoIcon: '/static/assets/branding-logo.svg',
      versionExpression: `${MessageService.translate(brazilLabelEnum.productVersion)}`,
    })
  }

  @OnUnmount({ target: Alias.PANEL_LOGIN })
  onUnmount(): void {
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
  }

  @OnCatch({ target: Alias.LOGIN_FOUNDATION })
  onCatch(event: RequestEvent<Error | Record<string, Value>>): void {
    const api = event.target as unknown as LoginAPI;
    const response = event.response as unknown as { response: Response };

    if (response.response?.status === 403) {
      api.setNotification(
        {
          highlight: true,
          level: ToastLevel.ERROR,
          message: MessageService.translate('login-message.blocked-user-text'),
          title: '',
        })
      return;
    }

    api.setNotification(
      {
        highlight: true,
        level: ToastLevel.ERROR,
        message: MessageService.translate('login-message.invalid-user-password-text'),
        title: '',
      });
  }

  @OnSuccess(Alias.LOGIN_FOUNDATION)
  onSuccess(): void {
    const token = AuthenticationService.getJWTToken() as JWTToken;
    const changePassword: string = token.claims[Token.CHANGE_PASSWORD] as string;
    if (changePassword === YesNoType.YES) {
      AuthenticationService.logout();
      ModalService.openDialog({
        closeable: true,
        dialogDescription: 'text.temporary-password',
        title: 'text.change-password',
      });
    }
  }

  @OnMount({ target: Alias.ESTABLISHMENT_TEXT })
  handleEstablishmentTextMount(event: MountEvent): void {
    const api = event.target as TextAPI;
    const fantasyName = this.context.get('fantasyName');
    api.setValue(fantasyName);
  }

  private async getEstablishmentDeault(): Promise<void> {
    await HttpService.get(Endpoint.ESTABLISHMENT_DEFAULT)
      .then(response => {
        const establishment = response.body as Establishment;
        const fantasyName = establishment?.fantasyName;
        if (fantasyName) {
          this.context.set({fantasyName});
          this.establishmentPanelAPI.setVisible(true);
        }
      })
  }

  private handleBrazilLabelData(): LocalizedLabelProps {
    const brazilLabelData = BRAZIL_LABEL_DATA;

    this.handleManifestJson()
      .then((response: ManifestJson) => {
        brazilLabelData.data[0].value = `${MessageService.translate(brazilLabelEnum.productText)} ${response.version}`;
        brazilLabelData.data[1].value = MessageService.translate(brazilLabelData.data[1].value);
        brazilLabelData.data[2].value = MessageService.translate(brazilLabelData.data[2].value);
        brazilLabelData.data[3].value = MessageService.translate(brazilLabelData.data[3].value);
        brazilLabelData.data[4].value = MessageService.translate(brazilLabelData.data[4].value);
        brazilLabelData.data[5].value = MessageService.translate(brazilLabelData.data[5].value);
        brazilLabelData.data[6].value = response.LOT ? response.LOT : MessageService.translate(UNSPECIFIED);
        brazilLabelData.data[7].value = response.date ? response.date : MessageService.translate(UNSPECIFIED);
        brazilLabelData.data[8].value = MessageService.translate(brazilLabelData.data[8].value);
      }).catch(() => {
        brazilLabelData.data[0].value = `${MessageService.translate(UNSPECIFIED)}`;
        brazilLabelData.data[6].value = MessageService.translate(UNSPECIFIED);
        brazilLabelData.data[7].value = MessageService.translate(UNSPECIFIED);
      });

    return brazilLabelData;
  }

  async handleManifestJson(): Promise<ManifestJson> {
    return await HttpService.get(MANIFEST)
      .then(response => {
        return response.body as ManifestJson;
      });
  }
}

export default LoginController;
